import { useEffect, useState } from 'react';
import type { Swiper as SwiperClass } from 'swiper';
import {
  A11y,
  EffectFade,
  Keyboard,
  Navigation,
  Pagination,
  Thumbs,
} from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import ZoomableImage from './ZoomableImage';
import useElementSize from './hooks/useElementSize';
import type { Size } from './hooks/useElementSize';
import 'swiper/css/bundle';
import './ProductCarousel.scss';

const PANE_MOUNT_SELECTOR = '[data-product-zoom-pane]';
const MAX_PANE_HEIGHT = 615;
const THUMBS_PER_VIEW = 4;

interface ProductImage {
  mainUrl: string;
  thumbUrl: string;
}

interface ProductBanner {
  text: string;
  colour: string;
}

interface ProductCarouselProps {
  images: ProductImage[];
  banner: ProductBanner | null;
}

function useZoomPane(slider: HTMLElement | null): {
  mount: HTMLElement | null;
  size: Size;
} {
  const [mount, setMount] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setMount(document.querySelector<HTMLElement>(PANE_MOUNT_SELECTOR));
  }, []);

  const mountSize = useElementSize(mount);
  const sliderSize = useElementSize(slider);

  return {
    mount,
    size: {
      width: mountSize?.width ?? 0,
      height: Math.min(sliderSize?.height ?? 0, MAX_PANE_HEIGHT),
    },
  };
}

export default function ProductCarousel({
  images,
  banner,
}: ProductCarouselProps) {
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperClass | null>(null);
  const [prevEl, setPrevEl] = useState<HTMLButtonElement | null>(null);
  const [nextEl, setNextEl] = useState<HTMLButtonElement | null>(null);
  const [paginationEl, setPaginationEl] = useState<HTMLDivElement | null>(null);
  const [slider, setSlider] = useState<HTMLDivElement | null>(null);

  const { mount: paneContainer, size: paneSize } = useZoomPane(slider);
  const hasGallery = images.length > 1;
  // A11y wires the arrows and bullets once, on init, so the slider has to wait
  // for the elements it is handed by ref or they never become keyboard targets.
  const controlsReady = !hasGallery || !!(prevEl && nextEl && paginationEl);

  return (
    <div className="sync-slider" ref={setSlider}>
      {controlsReady && (
        <Swiper
          className="sync-slider__for"
          modules={[A11y, EffectFade, Keyboard, Navigation, Pagination, Thumbs]}
          slidesPerView={1}
          speed={400}
          effect="fade"
          fadeEffect={{ crossFade: true }}
          thumbs={{ swiper: thumbsSwiper }}
          navigation={{ prevEl, nextEl, addIcons: false }}
          pagination={{ el: paginationEl, clickable: true }}
          keyboard={{ enabled: true, pageUpDown: false }}
          a11y={{
            containerRole: 'region',
            containerMessage: 'Product images',
            slideLabelMessage: 'Image {{index}} of {{slidesLength}}',
            prevSlideMessage: 'Previous image',
            nextSlideMessage: 'Next image',
            firstSlideMessage: 'This is the first image',
            lastSlideMessage: 'This is the last image',
            paginationBulletMessage: 'Go to image {{index}}',
          }}
        >
          {images.map((image, index) => (
            <SwiperSlide key={index}>
              <ZoomableImage
                src={image.mainUrl}
                className="sync-slider__image sync-slider__image--main"
                paneContainer={paneContainer}
                paneSize={paneSize}
              >
                {banner && (
                  <div className={`${banner.colour} product-banner`}>
                    {banner.text}
                  </div>
                )}
              </ZoomableImage>
            </SwiperSlide>
          ))}
        </Swiper>
      )}

      {hasGallery && (
        <div className="sync-slider__nav-row">
          <button
            type="button"
            className="swiper-button-prev"
            ref={setPrevEl}
          ></button>

          <Swiper
            className="sync-slider__nav"
            onSwiper={setThumbsSwiper}
            slidesPerView={THUMBS_PER_VIEW}
            spaceBetween={10}
            speed={400}
            watchSlidesProgress={true}
          >
            {images.map((image, index) => (
              <SwiperSlide key={index}>
                <div
                  className="sync-slider__image sync-slider__image--thumb"
                  style={{ backgroundImage: `url(${image.thumbUrl})` }}
                ></div>
              </SwiperSlide>
            ))}
          </Swiper>

          <button
            type="button"
            className="swiper-button-next"
            ref={setNextEl}
          ></button>
        </div>
      )}

      {hasGallery && (
        <div
          className="swiper-pagination sync-slider__pagination"
          ref={setPaginationEl}
        ></div>
      )}
    </div>
  );
}
